/**
 * Admin State Components
 * Loading, Error, and Empty state components for admin pages
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertCircle, 
  RefreshCw, 
  LogOut, 
  Inbox, 
  Search,
  Home,
  ShieldAlert
} from 'lucide-react';

/**
 * Admin Loading State
 */
export const AdminLoadingState = ({ 
  message = 'Loading...', 
  className = '',
  size = 'default' 
}) => {
  const sizeClasses = {
    small: 'py-8',
    default: 'py-16',
    large: 'min-h-[400px]',
    fullscreen: 'min-h-screen',
  };

  return (
    <div 
      className={`flex flex-col items-center justify-center ${sizeClasses[size]} ${className}`}
      data-testid="admin-loading-state"
    >
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-purple-500 border-t-transparent mb-4"></div>
      <p className="text-zinc-400">{message}</p>
    </div>
  );
};

/**
 * Admin Error State
 */
export const AdminErrorState = ({ 
  error,
  isAuthError = false,
  onRetry,
  onLogout,
  className = ''
}) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (onLogout) {
      onLogout();
    } else {
      navigate('/');
    }
  };

  const handleGoHome = () => {
    navigate('/admin');
  };

  return (
    <div 
      className={`flex items-center justify-center py-16 px-4 ${className}`}
      data-testid="admin-error-state"
    >
      <div className="bg-zinc-900 border border-red-500/30 rounded-xl p-8 max-w-md text-center">
        {isAuthError ? (
          <ShieldAlert className="w-14 h-14 text-amber-400 mx-auto mb-4" />
        ) : (
          <AlertCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
        )}
        
        <h3 className="text-lg font-bold mb-2" style={{ color: isAuthError ? '#fbbf24' : '#f87171' }}>
          {isAuthError ? 'Session Expired or Unauthorized' : 'Failed to Load Data'}
        </h3>
        
        <p className="text-zinc-400 mb-6">
          {error || (isAuthError 
            ? 'Your session may have expired or you may not have permission to access this page.'
            : 'An error occurred while loading the data. Please try again.'
          )}
        </p>
        
        <div className="flex gap-3 justify-center flex-wrap">
          {onRetry && (
            <button
              onClick={onRetry}
              className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
              data-testid="admin-error-retry-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          )}
          
          {isAuthError ? (
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
              data-testid="admin-error-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          ) : (
            <button
              onClick={handleGoHome}
              className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <Home className="w-4 h-4" />
              Admin Home
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Admin Empty State
 */
export const AdminEmptyState = ({ 
  title = 'No items found',
  message = 'There are no items to display.',
  icon: Icon = Inbox,
  action,
  actionLabel,
  className = ''
}) => {
  return (
    <div 
      className={`flex flex-col items-center justify-center py-16 px-4 ${className}`}
      data-testid="admin-empty-state"
    >
      <div className="bg-zinc-900/50 rounded-full p-6 mb-6">
        <Icon className="w-12 h-12 text-zinc-500" />
      </div>
      <h3 className="text-lg font-medium text-zinc-300 mb-2">{title}</h3>
      <p className="text-zinc-500 text-center max-w-md mb-6">{message}</p>
      {action && actionLabel && (
        <button
          onClick={action}
          className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
};

/**
 * Admin Search Empty State
 */
export const AdminSearchEmptyState = ({ 
  query,
  onClear,
  className = ''
}) => {
  return (
    <div 
      className={`flex flex-col items-center justify-center py-16 px-4 ${className}`}
      data-testid="admin-search-empty-state"
    >
      <Search className="w-12 h-12 text-zinc-500 mb-4" />
      <h3 className="text-lg font-medium text-zinc-300 mb-2">No results found</h3>
      <p className="text-zinc-500 text-center max-w-md mb-6">
        No items match &quot;{query}&quot;. Try adjusting your search or filters.
      </p>
      {onClear && (
        <button
          onClick={onClear}
          className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
        >
          Clear search
        </button>
      )}
    </div>
  );
};

export default {
  AdminLoadingState,
  AdminErrorState,
  AdminEmptyState,
  AdminSearchEmptyState,
};
